import {compact, uniq} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';

import useOnResize from 'lib/hooks/useOnResize';
import {useLatestRef} from 'lib/hooks/useLatestRef';

import type {IndicatorRegion} from '../store/IndicatorRegionStore';
import {
  resolveIndicatorScroller,
  selfAndAncestorsThrough
} from '../engine/annotationIndicatorPlacement';
import {FILTERED_ATTRIBUTES} from '../engine/annotationTextProjection';
import {observeSubtreeChanges} from '../engine/annotationUtils';

/**
 * Invokes `handler` when a region's measured geometry may have moved: anything
 * between the region and its pane resized, its rendered text settled after a
 * rewrite (MathJax, a translation swap), or its pane's DOM settled after a change
 * that resizes nothing (a reorder, a chip dropped into a blank) — settling on the
 * same debounce the paint uses.
 */
export default function useOnRegionsShift(
  regions: readonly IndicatorRegion[],
  handler: () => void
) {
  // Read through a ref, so a re-created handler cannot discard a settle mid-flight.
  const handlerRef = useLatestRef(handler);
  const onShift = useCallback(() => handlerRef.current(), [handlerRef]);

  const resizeTargets = useMemo(
    () =>
      regions.flatMap(({container}) => {
        const scroller = resolveIndicatorScroller(container);
        return scroller ? selfAndAncestorsThrough(container, scroller) : [];
      }),
    [regions]
  );
  useOnResize(resizeTargets, onShift);

  const panes = useMemo(
    () => uniq(compact(regions.map(({container}) => resolveIndicatorScroller(container)))),
    [regions]
  );
  useEffect(() => {
    const disconnects = panes.map((pane) => observeSubtreeChanges(pane, {onSettle: onShift}));
    return () => {
      disconnects.forEach((disconnect) => disconnect());
    };
  }, [panes, onShift]);

  useEffect(() => {
    if (regions.length === 0) {
      return undefined;
    }
    const disconnects = regions.map(({container}) =>
      observeSubtreeChanges(container, {onSettle: onShift}, {attributeFilter: FILTERED_ATTRIBUTES})
    );
    return () => {
      disconnects.forEach((disconnect) => disconnect());
    };
  }, [regions, onShift]);
}
